import { useNavigate } from "react-router-dom";
import {
    Stack, Card, CardActionArea, CardContent, Typography, Box, CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import React, { useState, useEffect } from "react";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import {
    selectionSidebar, isSelectionFinalized, selectionRoute, selectionResumeRoute,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";
import { useSelectionDraft } from "./SelectionSetupFlow";
import FactRow from "../../components/FactRow";

const EXPLORATION_LIST_PAGE_SIZE = 10000;

/** Marking-colour swatch for the exploration objective (same as the overview). */
function objectiveSwatch(name) {
    switch (name) {
        case "black_exploration":
            return { label: "Black", sx: { bgcolor: "#111827", border: "1px solid #111827" } };
        case "white_exploration":
            return { label: "White", sx: { bgcolor: "#ffffff", border: "1px solid #d1d5db" } };
        case "chromatic_exploration":
            return {
                label: "Chromatic",
                sx: {
                    background: "conic-gradient(#f87171, #fbbf24, #34d399, #60a5fa, #a78bfa, #f87171)",
                    border: "1px solid #d1d5db",
                },
            };
        default:
            return name
                ? { label: name, sx: { bgcolor: "#e5e7eb", border: "1px solid #d1d5db" } }
                : null;
    }
}

// Choose Explorations, in two places: the second step of the create wizard
// (/colorselections/<kind>/new/explorations, the draft names the laser and
// substrate; Next creates the selection) and the step of an existing
// selection (/colorselections/<kind>/<id>/explorations, reached with Back
// from the pruning; Next updates it).
function ColorSelectionSelectExplorations() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [creating, setCreating] = useState(false);
    const {
        selectionType, kind, selectionId: existingSelectionId, listRoute, setupRoute, stepRoute,
    } = useSelectionParams();
    // The wizard's draft (only under /new).
    const draft = useSelectionDraft()?.draft ?? null;
    // The laser and substrate the selection is based on: the Setup step's
    // choice, or the existing selection's row (the picker's filter and the
    // exploration list's).
    const [basis, setBasis] = useState(() => ({
        laserId: draft?.laserId || "",
        laserName: draft?.laserName || "",
        materialId: draft?.materialId || "",
        materialName: draft?.materialName || "",
    }));
    const { laserId, materialId } = basis;

    const toggleSelected = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // The explorations on the selection's laser and substrate.
    useEffect(() => {
        if (!laserId || !materialId) return undefined;
        let cancelled = false;
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const res = await axios.get(
                    `api/exploration/data?page=0&per_page=${EXPLORATION_LIST_PAGE_SIZE}&laser=${laserId}&material=${materialId}&sort_by=date&sort_order=desc`
                );
                if (!cancelled) setRows(res.data.data || []);
            } catch (err) {
                console.error(err);
                if (!cancelled) setRows([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [laserId, materialId]);

    // An existing selection: its explorations are ticked again and its basis
    // is the row's. A finished one can no longer change and goes to where it
    // continues.
    useEffect(() => {
        if (!existingSelectionId) return;
        axios
            .get(`api/color/selection/${existingSelectionId}/`)
            .then((res) => {
                const row = res.data || {};
                if (isSelectionFinalized(row.status)) {
                    navigate(selectionResumeRoute(existingSelectionId, row.status, row.selection_type), { replace: true });
                    return;
                }
                setSelectedIds((row.selected_exploration || []).map(String));
                setBasis({
                    laserId: row.laser_device || "",
                    laserName: row.laser_device_name || "",
                    materialId: row.material || "",
                    materialName: row.material_name || "",
                });
            })
            .catch((err) => console.error("Error loading the selection: " + err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingSelectionId]);

    const nextStep = kind.afterExplorationsStep;

    const nextPageClickFunc = async () => {
        if (existingSelectionId) {
            setCreating(true);
            try {
                await axios.patch(`api/color/selection/${existingSelectionId}/`, {
                    selected_exploration: selectedIds,
                });
                navigate(stepRoute(nextStep));
            } catch (err) {
                console.error("Error updating the selection: " + err);
            } finally {
                setCreating(false);
            }
            return;
        }
        setCreating(true);
        try {
            // Logo selections always run against the chromatic objective;
            // resolve its id here instead of a separate objective page.
            let objectiveId = null;
            await axios
                .get("api/color/selection/objective")
                .then((res) => {
                    const chromatic = (res.data || []).find((obj) => obj.name === "chromatic_selection");
                    if (chromatic) objectiveId = chromatic.id;
                })
                .catch((err) => console.error("Error fetching selection objectives: " + err));
            const selection_data = {
                name: draft?.name,
                description: draft?.description,
                selected_exploration: selectedIds,
                selection_objective: objectiveId,
                auth_user: localStorage.getItem("userId"),
                selection_type: selectionType,
                laser_device: laserId,
                material: materialId,
                ...(draft?.projectId ? { project: draft.projectId } : {}),
            };
            let createdId = null;
            await axios
                .post("api/color/selection/", selection_data)
                .then((res) => {
                    if (res.status === 201) createdId = res.data.id;
                })
                .catch((err) => console.error("Error creating the selection: " + err));
            if (createdId) {
                // The new selection's pages carry its id from here on.
                navigate(selectionRoute(selectionType, createdId, nextStep));
            }
        } finally {
            setCreating(false);
        }
    };

    const laserName = basis.laserName || "?";
    const materialName = basis.materialName || "?";

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title="Choose Explorations"
                subtitle={`Pick the explorations to source samples from — showing runs on ${laserName} / ${materialName}.`}
                stepSidebar={selectionSidebar("explorations", null, selectionType)}
                footerBelowPaper={
                    <Box sx={{ width: "100%" }}>
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={existingSelectionId ? listRoute : setupRoute("name")}
                            enableNextPage={selectedIds.length > 0 && !creating ? false : true}
                            nextPageClickFunc={nextPageClickFunc}
                        />
                    </Box>
                }
            >
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        {selectedIds.length} of {rows.length} exploration{rows.length === 1 ? "" : "s"} selected
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                        }}
                    >
                        {rows.map((row) => {
                            const selected = selectedIds.includes(row.id);
                            const swatch = objectiveSwatch(row.exploration_objective_name);
                            const createdDate = (() => {
                                if (!row.created_at) return "—";
                                const parsed = new Date(row.created_at);
                                if (Number.isNaN(parsed.getTime())) return row.created_at;
                                return parsed.toLocaleDateString(undefined, {
                                    year: "numeric", month: "short", day: "numeric",
                                });
                            })();
                            return (
                                <Card
                                    key={row.id}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 3,
                                        borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.08)",
                                        borderWidth: selected ? 2 : 1,
                                        bgcolor: "background.paper",
                                        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                                        minWidth: 0,
                                        overflow: "hidden",
                                        "&:hover": { boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)" },
                                    }}
                                >
                                    <CardActionArea onClick={() => toggleSelected(row.id)}>
                                        {/* dark header band: selected marker + name */}
                                        <Box sx={{ bgcolor: "#1b2a4a", px: 2, pt: 1.25, pb: 1.5 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600, color: "rgba(255,255,255,0.92)" }}
                                                >
                                                    {selected ? "Selected" : "Tap to select"}
                                                </Typography>
                                                {selected ? (
                                                    <CheckCircleIcon sx={{ color: "#4ade80" }} fontSize="small" />
                                                ) : (
                                                    <RadioButtonUncheckedIcon
                                                        sx={{ color: "rgba(255,255,255,0.5)" }}
                                                        fontSize="small"
                                                    />
                                                )}
                                            </Stack>
                                            <Typography
                                                component="div"
                                                sx={{
                                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                                    fontWeight: 700,
                                                    fontSize: "0.9rem",
                                                    color: "#ffffff",
                                                    lineHeight: 1.4,
                                                    wordBreak: "break-word",
                                                    mt: 0.25,
                                                }}
                                            >
                                                {row.name}
                                            </Typography>
                                        </Box>
                                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                            {swatch ? (
                                                <FactRow dense label="Marking colour">
                                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                                        <Box sx={{ width: 12, height: 12, borderRadius: 0.5, flexShrink: 0, ...swatch.sx }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {swatch.label}
                                                        </Typography>
                                                    </Stack>
                                                </FactRow>
                                            ) : null}
                                            <FactRow
                                                dense
                                                label="Iterations"
                                                value={`${row.current_iteration ?? 0}${row.iteration_count ? ` / ${row.iteration_count}` : ""}`}
                                            />
                                            {row.started_by_email ? (
                                                <FactRow dense label="Executor" value={row.started_by_email} />
                                            ) : null}
                                            <FactRow dense label="Created" value={createdDate} />
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Box>
                    {isLoading && (
                        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                            <Stack direction="column" alignItems="center" spacing={1.5}>
                                <CircularProgress size={28} />
                                <Typography>Loading explorations...</Typography>
                            </Stack>
                        </Box>
                    )}
                    {!isLoading && rows.length === 0 && (
                        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                            <Typography>
                                No explorations found for this laser/substrate combination.
                            </Typography>
                        </Box>
                    )}

                </Stack>
            </ExplorationPageShell>
        </Stack>
    );
}

export default ColorSelectionSelectExplorations;
